import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    AttachOrganizationSubscriptionRequest,
    fetchPolarisCapabilities,
    ProvisionOrganizationRequest,
    ProvisionOrganizationResponse,
    attachOrganizationSubscription,
    fetchOrganizationOpportunities,
    provisionOrganization,
} from "../services/polarisService";
import "../styles/polaris-admin.css";
import PolarisAdminSelect from "./PolarisAdminSelect";

type Mode = "checkout" | "attach";

type BillingInterval = "month" | "year" | "day";

type MetadataRow = { id: string; key: string; value: string };

const CURRENCY_OPTIONS = [
    { value: "usd", label: "USD — US Dollar" },
    { value: "cad", label: "CAD — Canadian Dollar" },
    { value: "gbp", label: "GBP — British Pound" },
    { value: "eur", label: "EUR — Euro" },
    { value: "aud", label: "AUD — Australian Dollar" },
    { value: "nzd", label: "NZD — New Zealand Dollar" },
] as const;

function newMetadataRow(key = "", value = ""): MetadataRow {
    return {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        key,
        value,
    };
}

function dollarsToCents(dollars: number | null): number | null {
    if (dollars == null || !Number.isFinite(dollars) || dollars <= 0) return null;
    return Math.round(dollars * 100);
}

const CURRENCY_LOCALES: Record<string, string> = {
    usd: "en-US",
    cad: "en-CA",
    gbp: "en-GB",
    eur: "en-IE",
    aud: "en-AU",
    nzd: "en-NZ",
};

function getCurrencySymbol(currencyCode: string): string {
    try {
        const parts = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currencyCode.toUpperCase(),
        }).formatToParts(0);
        return parts.find((part) => part.type === "currency")?.value ?? "$";
    } catch {
        return "$";
    }
}

function formatAmountForInput(amount: number, currencyCode: string): string {
    const locale = CURRENCY_LOCALES[currencyCode.toLowerCase()] ?? "en-US";
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function parseAmountInput(value: string): number | null {
    const cleaned = value.replace(/,/g, "").trim();
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 100) / 100;
}

function formatMoney(cents: number | null | undefined, currency?: string | null): string {
    if (cents == null) return "—";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: (currency || "usd").toUpperCase(),
        }).format(cents / 100);
    } catch {
        return `$${(cents / 100).toFixed(2)}`;
    }
}

/** Backend stores unlimited as -1; never show that sentinel in the UI. */
function formatMaxAthletes(value: number | null | undefined): string {
    if (value == null) return "—";
    if (value === -1) return "Unlimited";
    return String(value);
}

function formatOpportunityStatus(status: string | null | undefined): string {
    if (!status) return "—";
    return status
        .toLowerCase()
        .split("_")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
}

/** Badge reflects email delivery, not the sales-opportunity pipeline status. */
function formatResultBadge(result: ProvisionOrganizationResponse): string {
    if (result.checkoutUrl && result.checkoutInviteEmailSent === false) {
        return "Offer Not Sent";
    }
    return formatOpportunityStatus(result.status);
}

export default function PolarisProvisionOrganizationPage() {
    const [searchParams] = useSearchParams();
    const [mode, setMode] = useState<Mode>("checkout");
    const [organizationName, setOrganizationName] = useState("");
    const [adminEmail, setAdminEmail] = useState(searchParams.get("email") || "");
    const [adminFirstName, setAdminFirstName] = useState("");
    const [adminLastName, setAdminLastName] = useState("");
    const [amountDollars, setAmountDollars] = useState(4500);
    const [amountDraft, setAmountDraft] = useState("");
    const [amountFocused, setAmountFocused] = useState(false);
    const [currency, setCurrency] = useState("usd");
    const [billingInterval, setBillingInterval] = useState<BillingInterval>("year");
    const [shortBillingIntervalsEnabled, setShortBillingIntervalsEnabled] = useState(false);
    const [maxAthletes, setMaxAthletes] = useState("500");
    // Default to Unlimited when provisioning a new school; unticking reveals the 500 draft.
    const [unlimitedAthletes, setUnlimitedAthletes] = useState(true);
    const [trialPeriodDays, setTrialPeriodDays] = useState("");
    const [promotionCode, setPromotionCode] = useState("");
    const [doNotEmailCustomer, setDoNotEmailCustomer] = useState(false);
    const [metadataRows, setMetadataRows] = useState<MetadataRow[]>(() => [newMetadataRow("contractId", "")]);
    const [notes, setNotes] = useState("");
    const [notesMetadataOpen, setNotesMetadataOpen] = useState(false);
    const [stripeSubscriptionId, setStripeSubscriptionId] = useState("");
    const [linkClever, setLinkClever] = useState(false);
    const [cleverId, setCleverId] = useState("");
    const [cleverDistrictId, setCleverDistrictId] = useState("");
    const [cleverDistrictName, setCleverDistrictName] = useState("");
    const [cleverSchoolId, setCleverSchoolId] = useState("");
    const [cleverSchoolName, setCleverSchoolName] = useState("");
    const [enteredSchoolName, setEnteredSchoolName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ProvisionOrganizationResponse | null>(null);
    const [resultEmailSuppressed, setResultEmailSuppressed] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [opportunities, setOpportunities] = useState<ProvisionOrganizationResponse[]>([]);
    const [listError, setListError] = useState<string | null>(null);

    const sourceNotificationId = useMemo(() => {
        const raw = searchParams.get("notificationId");
        if (!raw) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    }, [searchParams]);

    const loadOpportunities = useCallback(async () => {
        try {
            setListError(null);
            const page = await fetchOrganizationOpportunities(0, 20);
            setOpportunities(page.content || []);
        } catch (e: any) {
            setListError(e?.message || "Failed to load opportunities");
        }
    }, []);

    useEffect(() => {
        void loadOpportunities();
    }, [loadOpportunities]);

    useEffect(() => {
        let cancelled = false;
        fetchPolarisCapabilities()
            .then((caps) => {
                if (!cancelled) {
                    setShortBillingIntervalsEnabled(caps.shortBillingIntervalsEnabled);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setShortBillingIntervalsEnabled(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const email = searchParams.get("email");
        if (email) {
            setAdminEmail(email);
        }
    }, [searchParams]);

    const billingIntervalOptions = useMemo(
        () => [
            { value: "year", label: "Year" },
            { value: "month", label: "Month" },
            ...(shortBillingIntervalsEnabled ? [{ value: "day", label: "Day" }] : []),
        ],
        [shortBillingIntervalsEnabled],
    );

    const buildMetadata = (): Record<string, string> => {
        const out: Record<string, string> = {};
        const seen = new Set<string>();
        for (const row of metadataRows) {
            const key = row.key.trim();
            const value = row.value.trim();
            if (!key && !value) continue;
            if (!key) {
                throw new Error("Each metadata attribute needs a key, or clear the empty row.");
            }
            if (!value) continue;
            if (seen.has(key)) {
                throw new Error(`Duplicate metadata key “${key}”.`);
            }
            seen.add(key);
            out[key] = value;
        }
        return out;
    };

    const updateMetadataRow = (id: string, field: "key" | "value", next: string) => {
        setMetadataRows((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: next } : row)));
    };

    const removeMetadataRow = (id: string) => {
        setMetadataRows((rows) => (rows.length <= 1 ? [newMetadataRow()] : rows.filter((row) => row.id !== id)));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setResult(null);
        setLinkCopied(false);
        setSubmitting(true);
        try {
            const orgName = organizationName.trim();
            const email = adminEmail.trim();
            const firstName = adminFirstName.trim();
            const lastName = adminLastName.trim();

            if (!orgName) {
                throw new Error("Organization name is required.");
            }
            if (!email) {
                throw new Error("Admin email is required.");
            }
            if (!firstName) {
                throw new Error("Admin first name is required.");
            }
            if (!lastName) {
                throw new Error("Admin last name is required.");
            }

            const metadata = buildMetadata();
            let athletes: number;
            if (unlimitedAthletes) {
                athletes = -1;
            } else {
                athletes = Number(maxAthletes);
                if (!Number.isFinite(athletes) || !Number.isInteger(athletes) || athletes < 0) {
                    throw new Error("Enter a whole number of athletes (0 or more), or choose Unlimited.");
                }
            }

            const cleverFields = linkClever
                ? {
                      cleverId: cleverId.trim() || undefined,
                      cleverDistrictId: cleverDistrictId.trim() || undefined,
                      cleverDistrictName: cleverDistrictName.trim() || undefined,
                      cleverSchoolId: cleverSchoolId.trim() || undefined,
                      cleverSchoolName: cleverSchoolName.trim() || undefined,
                      enteredSchoolName: enteredSchoolName.trim() || organizationName.trim() || undefined,
                  }
                : {};

            if (linkClever) {
                if (!cleverFields.cleverId) {
                    throw new Error("Clever user ID is required when linking Clever.");
                }
                if (!cleverFields.cleverDistrictId) {
                    throw new Error("Clever district ID is required when linking Clever.");
                }
                if (!cleverFields.enteredSchoolName) {
                    throw new Error("School name is required when linking Clever.");
                }
            }

            if (mode === "checkout") {
                let currentAmount = amountDollars;
                if (amountFocused) {
                    const cleaned = amountDraft.replace(/,/g, "").trim();
                    if (cleaned) {
                        currentAmount = parseAmountInput(amountDraft) ?? Number(cleaned);
                    }
                }
                const amountCents = dollarsToCents(Number.isFinite(currentAmount) ? currentAmount : null);
                if (amountCents == null) {
                    throw new Error("Enter a valid positive price.");
                }
                const trimmedPromo = promotionCode.trim();
                const trialDays = trialPeriodDays ? Number(trialPeriodDays) : null;
                if (trimmedPromo && trialDays != null && trialDays > 0) {
                    throw new Error("Use either trial days or a promotional code, not both.");
                }
                const body: ProvisionOrganizationRequest = {
                    organizationName: orgName,
                    adminEmail: email,
                    adminFirstName: firstName,
                    adminLastName: lastName,
                    amountCents,
                    currency: currency.trim().toLowerCase() || "usd",
                    billingInterval,
                    intervalCount: 1,
                    maxAthletes: athletes,
                    metadata,
                    trialPeriodDays: trialDays,
                    promotionCode: trimmedPromo || undefined,
                    sendCheckoutInviteEmail: !doNotEmailCustomer,
                    sourceNotificationId,
                    notes: notes.trim() || undefined,
                    ...cleverFields,
                };
                setResultEmailSuppressed(doNotEmailCustomer);
                setResult(await provisionOrganization(body));
            } else {
                const body: AttachOrganizationSubscriptionRequest = {
                    organizationName: orgName,
                    adminEmail: email,
                    adminFirstName: firstName,
                    adminLastName: lastName,
                    stripeSubscriptionId: stripeSubscriptionId.trim(),
                    maxAthletesOverride: athletes,
                    metadata,
                    sourceNotificationId,
                    notes: notes.trim() || undefined,
                    ...cleverFields,
                };
                setResultEmailSuppressed(false);
                setResult(await attachOrganizationSubscription(body));
            }
            await loadOpportunities();
        } catch (err: any) {
            setError(err?.message || "Provisioning failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="pa-provision" data-testid="polaris-provision-page">
            <div className="pa-provision-intro">
                <div>
                    <h2 className="pa-provision-heading">Provision organization</h2>
                    <p className="pa-muted pa-provision-desc">
                        Create a private Stripe offer after a demo, or attach a Dashboard-created subscription.
                        Optional Clever fields link SSO when checkout completes or when attaching.
                    </p>
                </div>
                <div className="pa-segmented" role="group" aria-label="Provisioning mode">
                    <button
                        type="button"
                        className={`pa-segmented-btn${mode === "checkout" ? " active" : ""}`}
                        onClick={() => setMode("checkout")}
                    >
                        Custom offer
                    </button>
                    <button
                        type="button"
                        className={`pa-segmented-btn${mode === "attach" ? " active" : ""}`}
                        onClick={() => setMode("attach")}
                    >
                        Attach subscription
                    </button>
                </div>
            </div>

            <form className="pa-provision-form" onSubmit={handleSubmit} data-testid="provision-form">
                <section className="pa-provision-card">
                    <h3 className="pa-provision-card-title">Organization</h3>
                    <div className="pa-form-grid pa-form-grid--wide">
                        <label>
                            Organization / school name
                            <input
                                required
                                value={organizationName}
                                onChange={(e) => setOrganizationName(e.target.value)}
                                placeholder="Austin High School"
                                data-testid="provision-org-name"
                            />
                        </label>
                        <label>
                            Admin email
                            <input
                                required
                                type="email"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                placeholder="admin@school.edu"
                                data-testid="provision-admin-email"
                            />
                        </label>
                        <label>
                            Admin first name
                            <input
                                required
                                value={adminFirstName}
                                onChange={(e) => setAdminFirstName(e.target.value)}
                                data-testid="provision-admin-first-name"
                            />
                        </label>
                        <label>
                            Admin last name
                            <input
                                required
                                value={adminLastName}
                                onChange={(e) => setAdminLastName(e.target.value)}
                                data-testid="provision-admin-last-name"
                            />
                        </label>
                    </div>
                </section>

                <section className="pa-provision-card">
                    <h3 className="pa-provision-card-title">
                        {mode === "checkout" ? "Offer details" : "Stripe subscription"}
                    </h3>
                    <div className="pa-form-grid pa-form-grid--wide">
                        {mode === "checkout" ? (
                            <>
                                <label>
                                    Price
                                    <div className="pa-money-input">
                                        <span className="pa-money-input-prefix" aria-hidden="true">
                                            {getCurrencySymbol(currency)}
                                        </span>
                                        <input
                                            value={
                                                amountFocused
                                                    ? amountDraft
                                                    : formatAmountForInput(amountDollars, currency)
                                            }
                                            onFocus={() => {
                                                setAmountFocused(true);
                                                setAmountDraft(String(amountDollars));
                                            }}
                                            onBlur={() => {
                                                const cleaned = amountDraft.replace(/,/g, "").trim();
                                                if (cleaned) {
                                                    const parsed = parseAmountInput(amountDraft);
                                                    if (parsed != null) {
                                                        setAmountDollars(parsed);
                                                    } else {
                                                        const raw = Number(cleaned);
                                                        setAmountDollars(Number.isFinite(raw) ? raw : amountDollars);
                                                    }
                                                }
                                                setAmountFocused(false);
                                            }}
                                            onChange={(e) => setAmountDraft(e.target.value)}
                                            inputMode="decimal"
                                            placeholder="0.00"
                                            data-testid="provision-amount"
                                        />
                                    </div>
                                </label>
                                <label>
                                    Currency
                                    <PolarisAdminSelect
                                        value={currency}
                                        options={CURRENCY_OPTIONS.map((opt) => ({
                                            value: opt.value,
                                            label: opt.label,
                                        }))}
                                        onChange={setCurrency}
                                        data-testid="provision-currency"
                                    />
                                </label>
                                <label>
                                    Billing interval
                                    <PolarisAdminSelect
                                        value={billingInterval}
                                        options={billingIntervalOptions}
                                        onChange={(next) => setBillingInterval(next as BillingInterval)}
                                        data-testid="provision-billing-interval"
                                    />
                                </label>
                                <label>
                                    Trial days (optional)
                                    <input
                                        value={trialPeriodDays}
                                        onChange={(e) => setTrialPeriodDays(e.target.value)}
                                        inputMode="numeric"
                                        data-testid="provision-trial-days"
                                    />
                                </label>
                                <label className="pa-span-2">
                                    Promotional code (optional)
                                    <input
                                        value={promotionCode}
                                        onChange={(e) => setPromotionCode(e.target.value)}
                                        placeholder="e.g. PILOT100"
                                        autoComplete="off"
                                        data-testid="provision-promotion-code"
                                    />
                                    <span className="pa-muted" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                                        Pre-applies an active Stripe promotion code to checkout. If left blank, the
                                        customer can optionally enter a code on the Stripe checkout page. Once coupons
                                        discount the first invoice only; Forever/Repeating coupons apply per Stripe
                                        coupon settings. Do not combine with trial days.
                                    </span>
                                </label>
                                <div className="pa-span-2">
                                    <label className="pa-checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={doNotEmailCustomer}
                                            onChange={(e) => setDoNotEmailCustomer(e.target.checked)}
                                            data-testid="provision-do-not-email-customer"
                                        />
                                        Do not email this offer to the customer
                                    </label>
                                    <span className="pa-muted" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                                        No automated emails are sent to the customer for this offer — not the checkout
                                        invite, and not the abandonment reminder if the link expires. Share the checkout
                                        link with the partner yourself using Continue to checkout or Copy invite.
                                    </span>
                                </div>
                            </>
                        ) : (
                            <label className="pa-span-full">
                                Stripe subscription ID
                                <input
                                    required
                                    value={stripeSubscriptionId}
                                    onChange={(e) => setStripeSubscriptionId(e.target.value)}
                                    placeholder="sub_..."
                                    data-testid="provision-stripe-sub-id"
                                />
                            </label>
                        )}

                        <label
                            className={`pa-athlete-limit ${mode === "checkout" ? "pa-span-2" : "pa-span-full"}`}
                            data-testid="provision-athlete-limit"
                        >
                            Athlete limit
                            <div className="pa-athlete-limit-controls">
                                {unlimitedAthletes ? (
                                    <span className="pa-athlete-limit-unlimited">Unlimited</span>
                                ) : (
                                    <input
                                        required
                                        value={maxAthletes}
                                        onChange={(e) => setMaxAthletes(e.target.value)}
                                        inputMode="numeric"
                                        min={0}
                                        step={1}
                                        placeholder="e.g. 500"
                                        data-testid="provision-max-athletes"
                                    />
                                )}
                                <label className="pa-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={unlimitedAthletes}
                                        onChange={(e) => setUnlimitedAthletes(e.target.checked)}
                                        data-testid="provision-unlimited-athletes"
                                    />
                                    Unlimited athletes
                                </label>
                            </div>
                        </label>
                    </div>
                </section>

                <section className="pa-provision-card" data-testid="provision-clever-section">
                    <div className="pa-provision-card-header">
                        <h3 className="pa-provision-card-title">Clever SSO</h3>
                        <label className="pa-checkbox-row">
                            <input
                                type="checkbox"
                                checked={linkClever}
                                onChange={(e) => setLinkClever(e.target.checked)}
                                data-testid="provision-link-clever"
                            />
                            Link Clever account
                        </label>
                    </div>
                    {linkClever ? (
                        <div className="pa-form-grid pa-form-grid--wide">
                            <label>
                                Clever user ID
                                <input
                                    required
                                    value={cleverId}
                                    onChange={(e) => setCleverId(e.target.value)}
                                    placeholder="Clever user object ID"
                                    data-testid="provision-clever-id"
                                />
                            </label>
                            <label>
                                Clever district ID
                                <input
                                    required
                                    value={cleverDistrictId}
                                    onChange={(e) => setCleverDistrictId(e.target.value)}
                                    placeholder="Clever district object ID"
                                    data-testid="provision-clever-district-id"
                                />
                            </label>
                            <label>
                                Clever district name
                                <input
                                    value={cleverDistrictName}
                                    onChange={(e) => setCleverDistrictName(e.target.value)}
                                    placeholder="Optional"
                                />
                            </label>
                            <label>
                                Clever school object ID
                                <input
                                    value={cleverSchoolId}
                                    onChange={(e) => setCleverSchoolId(e.target.value)}
                                    placeholder="Optional"
                                />
                            </label>
                            <label>
                                Clever school name
                                <input
                                    value={cleverSchoolName}
                                    onChange={(e) => setCleverSchoolName(e.target.value)}
                                    placeholder="Optional"
                                />
                            </label>
                            <label>
                                School name for subscription
                                <input
                                    value={enteredSchoolName}
                                    onChange={(e) => setEnteredSchoolName(e.target.value)}
                                    placeholder="Defaults to organization name"
                                    data-testid="provision-entered-school-name"
                                />
                            </label>
                        </div>
                    ) : (
                        <p className="pa-muted" style={{ margin: 0 }}>
                            Leave unchecked unless this organization should sign in with Clever.
                        </p>
                    )}
                </section>

                <section className="pa-provision-card pa-provision-card--collapsible" data-testid="provision-notes-metadata-section">
                    <button
                        type="button"
                        className="pa-provision-card-toggle"
                        onClick={() => setNotesMetadataOpen((open) => !open)}
                        aria-expanded={notesMetadataOpen}
                        aria-controls="provision-notes-metadata-panel"
                        data-testid="provision-notes-metadata-toggle"
                    >
                        <h3 className="pa-provision-card-title">Notes & metadata</h3>
                        <span className={`pa-provision-chevron${notesMetadataOpen ? " open" : ""}`} aria-hidden="true">
                            ▶
                        </span>
                    </button>
                    {notesMetadataOpen && (
                        <div className="pa-provision-card-body" id="provision-notes-metadata-panel">
                    <div className="pa-metadata-block" data-testid="provision-metadata">
                        <div className="pa-metadata-header">
                            <p className="pa-metadata-label">Extra metadata</p>
                            <p className="pa-muted pa-metadata-help">
                                Optional key/value attributes stored on the opportunity (for example contract ID).
                            </p>
                        </div>
                        <div className="pa-metadata-rows">
                            {metadataRows.map((row) => (
                                <div className="pa-metadata-row" key={row.id}>
                                    <label>
                                        Key
                                        <input
                                            value={row.key}
                                            onChange={(e) => updateMetadataRow(row.id, "key", e.target.value)}
                                            placeholder="contractId"
                                            data-testid="provision-metadata-key"
                                        />
                                    </label>
                                    <label>
                                        Value
                                        <input
                                            value={row.value}
                                            onChange={(e) => updateMetadataRow(row.id, "value", e.target.value)}
                                            placeholder="Optional"
                                            data-testid="provision-metadata-value"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className="pa-metadata-remove"
                                        onClick={() => removeMetadataRow(row.id)}
                                        aria-label="Remove metadata attribute"
                                        data-testid="provision-metadata-remove"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="pa-btn pa-btn-secondary"
                            onClick={() => setMetadataRows((rows) => [...rows, newMetadataRow()])}
                            data-testid="provision-metadata-add"
                        >
                            Add attribute
                        </button>
                    </div>
                    <label className="pa-metadata-notes">
                        Notes
                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </label>
                        </div>
                    )}
                </section>

                {error && <div className="pa-error" data-testid="provision-error">{error}</div>}

                {result && (
                    <div className="pa-provision-result" data-testid="provision-result" role="status">
                        <div className="pa-provision-result-header">
                            <div className="pa-provision-result-icon" aria-hidden="true">
                                ✓
                            </div>
                            <div className="pa-provision-result-heading">
                                <h3 className="pa-provision-result-title">
                                    {result.checkoutUrl ? "Offer created" : "Subscription attached"}
                                </h3>
                                <p className="pa-provision-result-subtitle">
                                    {result.checkoutUrl
                                        ? result.checkoutInviteEmailSent
                                            ? `Checkout invite emailed to ${result.adminEmail}.`
                                            : resultEmailSuppressed
                                                ? `Offer is ready. Checkout invite was not emailed to ${result.adminEmail}. Use Continue to checkout or Copy invite to share it.`
                                                : `Offer is ready, but the invite email to ${result.adminEmail} could not be sent. Use Continue to checkout or Copy invite.`
                                        : `${result.organizationName} is provisioned and linked to Stripe.`}
                                </p>
                            </div>
                            <span
                                className="pa-provision-result-badge"
                                data-testid="provision-result-badge"
                            >
                                {formatResultBadge(result)}
                            </span>
                        </div>

                        <dl className="pa-provision-result-facts">
                            <div>
                                <dt>Organization</dt>
                                <dd>{result.organizationName}</dd>
                            </div>
                            <div>
                                <dt>Athlete limit</dt>
                                <dd>{formatMaxAthletes(result.maxAthletes)}</dd>
                            </div>
                            {result.amountCents != null && (
                                <div>
                                    <dt>Offer price</dt>
                                    <dd>{formatMoney(result.amountCents, result.currency)}</dd>
                                </div>
                            )}
                            {result.promotionCode && (
                                <div>
                                    <dt>Promotional code</dt>
                                    <dd>{result.promotionCode}</dd>
                                </div>
                            )}
                            <div>
                                <dt>Opportunity</dt>
                                <dd>#{result.opportunityId}</dd>
                            </div>
                            <div>
                                <dt>Company</dt>
                                <dd>#{result.companyId}</dd>
                            </div>
                            <div>
                                <dt>Admin user</dt>
                                <dd>#{result.adminUserId}</dd>
                            </div>
                        </dl>

                        {result.checkoutUrl && (
                            <div className="pa-provision-result-checkout">
                                <div>
                                    <p className="pa-provision-result-checkout-label">Ready for payment</p>
                                    <p className="pa-muted pa-provision-result-checkout-help">
                                        Continue to Stripe to complete the organization subscription, or copy the
                                        invite for the school admin.
                                    </p>
                                </div>
                                <div className="pa-provision-result-actions">
                                    <button
                                        type="button"
                                        className="pa-provision-subscribe-btn"
                                        data-testid="provision-open-checkout"
                                        onClick={() => {
                                            window.open(result.checkoutUrl!, "_blank", "noopener,noreferrer");
                                        }}
                                    >
                                        Continue to checkout
                                    </button>
                                    <button
                                        type="button"
                                        className="pa-provision-result-btn pa-provision-result-btn--secondary"
                                        data-testid="provision-copy-checkout"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(result.checkoutUrl!);
                                                setLinkCopied(true);
                                                window.setTimeout(() => setLinkCopied(false), 2000);
                                            } catch {
                                                setError(
                                                    "Could not copy the invite. Use Continue to checkout instead."
                                                );
                                            }
                                        }}
                                    >
                                        {linkCopied ? "Copied" : "Copy invite"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="pa-provision-actions">
                    <button
                        type="submit"
                        className="pa-primary-btn"
                        disabled={submitting}
                        data-testid="provision-submit"
                    >
                        {submitting
                            ? "Working…"
                            : mode === "checkout"
                              ? "Create offer"
                              : "Attach subscription"}
                    </button>
                </div>
            </form>

            <section className="pa-provision-card pa-provision-opportunities">
                <h3 className="pa-provision-card-title">Recent opportunities</h3>
                {listError && <div className="pa-error">{listError}</div>}
                <div className="pa-table-wrapper">
                    <table className="pa-table">
                        <thead>
                            <tr>
                                <th>Organization</th>
                                <th>Admin</th>
                                <th>Status</th>
                                <th>Price</th>
                                <th>Max athletes</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {opportunities.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="pa-muted">
                                        No opportunities yet.
                                    </td>
                                </tr>
                            ) : (
                                opportunities.map((op) => (
                                    <tr key={op.opportunityId}>
                                        <td>{op.organizationName}</td>
                                        <td>{op.adminEmail}</td>
                                        <td>{op.status}</td>
                                        <td>{formatMoney(op.amountCents, op.currency)}</td>
                                        <td>{formatMaxAthletes(op.maxAthletes)}</td>
                                        <td>
                                            {op.checkoutUrl ? (
                                                <button
                                                    type="button"
                                                    className="pa-provision-table-subscribe-btn"
                                                    data-testid="provision-opportunity-checkout"
                                                    onClick={() => {
                                                        window.open(
                                                            op.checkoutUrl!,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        );
                                                    }}
                                                >
                                                    Continue to checkout
                                                </button>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
